import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { uniqueId } from '@ember/helper';
import Component from '@glimmer/component';
import FileDropzone from 'ember-file-upload/components/file-dropzone';
import fileQueue from 'ember-file-upload/helpers/file-queue';
import type { UploadFile, Queue } from 'ember-file-upload';
import { PhCloudArrowUp } from 'ember-phosphor-icons';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import type FeaturesService from '../services/features.ts';

export interface FileUploaderSignature<T> {
  Args: {
    disabled?: boolean;
    isPublic?: boolean;
    // Is public image removes the need for a signed URL
    isPublicImage?: boolean;
    accept?: string;
    /** Max file size in bytes. Larger files are rejected before the S3 upload
     * starts, with an inline error naming the file. */
    maxFileSize?: number;
    /** Enforce @accept at the queue level: files that don't match are rejected
     * before the S3 upload starts, with an inline error naming the file. The
     * `accept` attribute alone only filters the native picker — drag-and-drop
     * bypasses it. */
    enforceAccept?: boolean;
    /** Suppress the built-in rejection messages (rendered inside this
     * component's wrapper, which can distort tight layouts like a button
     * column). Pair with the yielded typeRejectedFiles/sizeRejectedFiles to
     * render them where they fit. */
    hideRejectionErrors?: boolean;
    /** Override the generic digital-asset upload flow while retaining this
     * component's picker, dropzone, type, size, and concurrency handling. */
    uploadFile?: (file: File) => Promise<T>;
    didUploadFile?: (content: T) => void;
    useDropzone?: boolean;
  };
  Blocks: {
    default: [
      {
        queue: Queue;
        uniqueId: string;
        uploadTask: { isRunning: boolean };
        typeRejectedFiles: string[];
        sizeRejectedFiles: string[];
        acceptedTypesDisplay: string;
        maxFileSizeMb: number;
      },
    ];
  };
  Element: HTMLDivElement;
}

/** True when the file matches one of the accept-string tokens
 * (`.ext`, `type/subtype`, or `type/*`). */
function fileMatchesAccept(file: File, accept: string) {
  const tokens = accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  if (!tokens.length) return true;

  const name = file.name.toLowerCase();
  const mime = (file.type || '').toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return mime.startsWith(token.slice(0, -1));
    return mime === token;
  });
}

export default class FileUploaderComponent<T> extends Component<FileUploaderSignature<T>> {
  @service declare features: FeaturesService;
  @tracked isFileDialogOpen = false;

  /** Files from the current selection/drop that failed the @maxFileSize check. */
  @tracked sizeRejectedFiles: string[] = [];

  /** Files from the current selection/drop that failed the @accept check. */
  @tracked typeRejectedFiles: string[] = [];

  /** True while the adds from a single selection/drop are being processed —
   * they all arrive in one synchronous loop, so a microtask outlives the batch. */
  #inSelectionBatch = false;

  get maxFileSizeMb() {
    return Math.round(((this.args.maxFileSize ?? 0) / (1024 * 1024)) * 10) / 10;
  }

  /** @accept formatted for hints and errors: '.pdf,.png' → 'PDF, PNG'. */
  get acceptedTypesDisplay() {
    const accept = this.args.accept;
    if (!accept) return '';
    return accept
      .split(',')
      .map((token) => {
        const trimmed = token.trim();
        return trimmed.startsWith('.') ? trimmed.slice(1).toUpperCase() : trimmed;
      })
      .filter(Boolean)
      .join(', ');
  }

  get isPublic() {
    return !!this.args.isPublic;
  }

  // WarpDrive migration Phase 2.4: buildBaseURL replaces adapter.urlForCreateRecord.
  get collectionPath() {
    const resourcePath = this.isPublic ? 'public-assets' : 'digital-assets';
    const opts: QueryUrlOptions = {
      identifier: { type: resourcePath },
      op: 'query',
      resourcePath,
    };
    return buildBaseURL(opts);
  }

  // WarpDrive migration Phase 2.4: buildTioAuthHeaders replaces adapter.headers
  // for direct fetch calls (presigned-S3 upload path).
  get headers() {
    return buildTioAuthHeaders({
      apiKey: this.features.ENV?.apiKey ?? '',
      contentType: 'application/vnd.api+json',
    });
  }

  newUploadAsset = (upload: UploadFile) => {
    // First file of a new selection/drop: clear the previous batch's rejections.
    if (!this.#inSelectionBatch) {
      this.#inSelectionBatch = true;
      this.sizeRejectedFiles = [];
      this.typeRejectedFiles = [];
      queueMicrotask(() => (this.#inSelectionBatch = false));
    }

    const accept = this.args.accept;
    if (this.args.enforceAccept && accept && !fileMatchesAccept(upload.file, accept)) {
      // Drop the file before requesting a signed URL — nothing gets uploaded.
      upload.queue?.remove(upload);
      if (!this.typeRejectedFiles.includes(upload.file.name)) {
        this.typeRejectedFiles = [...this.typeRejectedFiles, upload.file.name];
      }
      this.isFileDialogOpen = false;
      return;
    }

    const max = this.args.maxFileSize;
    if (max && upload.file.size > max) {
      // Drop the file before requesting a signed URL — nothing gets uploaded.
      upload.queue?.remove(upload);
      if (!this.sizeRejectedFiles.includes(upload.file.name)) {
        this.sizeRejectedFiles = [...this.sizeRejectedFiles, upload.file.name];
      }
      this.isFileDialogOpen = false;
      return;
    }

    this.uploadAsset.perform(upload);
    this.isFileDialogOpen = false;
  };

  uploadAsset = task({ maxConcurrency: 3, enqueue: true }, async (queue: UploadFile) => {
    if (this.args.uploadFile) {
      try {
        const content = await this.args.uploadFile(queue.file);
        await this.args.didUploadFile?.(content);
      } finally {
        queue.queue?.remove(queue);
      }
      return;
    }

    const apiResponse = await fetch(`${this.collectionPath}/s3-signed-url`, {
      headers: this.headers,
      method: 'POST',
      body: JSON.stringify({ filename: queue.file.name, public_image: this.args.isPublicImage }),
    });
    // A non-2xx would parse to a body with no `url` and the PUT would target
    // `undefined` — fail here, naming the request, instead (#10068 item 3).
    if (!apiResponse.ok) {
      throw new Error(
        `Signed-URL request for "${queue.file.name}" failed with status ${apiResponse.status}`
      );
    }
    const content = await apiResponse.json();

    await queue.uploadBinary(content.url, {
      method: 'PUT',
    });
    await this.args.didUploadFile?.(content);
  });

  openFileDialog = (event: Event) => {
    const fileInput = event.target as HTMLInputElement;
    this.isFileDialogOpen = true;

    fileInput!.addEventListener(
      'cancel',
      () => {
        this.fileSelectedCanceled();
      },
      { once: true }
    );
  };

  fileSelectedCanceled = () => {
    this.isFileDialogOpen = false;
  };

  <template>
    <div ...attributes>
      {{#let (uniqueId) as |uniqueId|}}
        {{#let
          (fileQueue name=(concat "upload-" uniqueId) onFileAdded=this.newUploadAsset)
          as |queue|
        }}
          {{#if (has-block)}}
            {{yield
              (hash
                queue=queue
                uniqueId=uniqueId
                uploadTask=this.uploadAsset
                typeRejectedFiles=this.typeRejectedFiles
                sizeRejectedFiles=this.sizeRejectedFiles
                acceptedTypesDisplay=this.acceptedTypesDisplay
                maxFileSizeMb=this.maxFileSizeMb
              )
            }}
          {{else if @useDropzone}}
            {{#unless @disabled}}
              <FileDropzone
                class="tio-place-center h-full"
                @queue={{queue}}
                @onDrop={{this.fileSelectedCanceled}}
                as |dropzone|
              >
                <div class="w-full">
                  {{#if this.isFileDialogOpen}}
                    <div id="drag-window" class="fixed inset-0 z-40 tio-bg-sunken opacity-50"></div>
                  {{/if}}
                  <div class="tio-row-center w-full">
                    <label
                      for={{uniqueId}}
                      class="tio-place-center w-full h-48 border-2 tio-border-default border-dashed cursor-pointer tio-surface-sunken hover:tio-bg-sunken-hover"
                      {{on "click" this.openFileDialog}}
                    >
                      {{#if dropzone.active}}
                        {{t "file_upload.drop_to_upload"}}
                      {{else if queue.files.length}}
                        {{t "file_upload.uploading"}}
                        {{queue.files.length}}
                        {{t "file_upload.files"}}
                        ({{queue.progress}}%)
                      {{else if dropzone.supported}}
                        <div class="tio-place-center pt-5 pb-6">
                          <PhCloudArrowUp @size="32" class="mb-2" />
                          <p class="mb-2 text-sm tio-fg-muted"><span class="font-semibold">{{t
                                "file_upload.select_a_file"
                              }}</span>
                            {{t "file_upload.or_drag_and_drop"}}</p>
                          {{#if (or this.acceptedTypesDisplay @maxFileSize)}}
                            <p class="tio-text-caption" data-test-file-uploader-constraints-hint>
                              {{this.acceptedTypesDisplay}}{{#if
                                (and this.acceptedTypesDisplay @maxFileSize)
                              }}
                                ·
                              {{/if}}{{#if @maxFileSize}}{{t
                                  "file_upload.max_file_size"
                                  maxSize=this.maxFileSizeMb
                                  default="Max file size {maxSize}MB"
                                }}{{/if}}
                            </p>
                          {{/if}}
                        </div>
                      {{/if}}
                      <input
                        id={{uniqueId}}
                        type="file"
                        {{queue.selectFile}}
                        accept={{@accept}}
                        class="hidden"
                        multiple="true"
                        {{on "click" this.openFileDialog}}
                        {{on "change" this.fileSelectedCanceled}}
                      />
                    </label>
                  </div>
                </div>
              </FileDropzone>
            {{/unless}}
          {{else}}
            <input
              id={{uniqueId}}
              type="file"
              {{queue.selectFile}}
              accept={{@accept}}
              class=""
              multiple="true"
            />
          {{/if}}
        {{/let}}
      {{/let}}
      {{#unless @hideRejectionErrors}}
        {{#each this.typeRejectedFiles as |fileName|}}
          <p class="tio-text-caption tio-fg-danger mt-1" data-test-file-uploader-type-error>
            {{t
              "file_upload.file_type_not_allowed"
              fileName=fileName
              types=this.acceptedTypesDisplay
              default="{fileName} isn't an accepted file type ({types})."
            }}
          </p>
        {{/each}}
        {{#each this.sizeRejectedFiles as |fileName|}}
          <p class="tio-text-caption tio-fg-danger mt-1" data-test-file-uploader-size-error>
            {{t
              "file_upload.file_too_large"
              fileName=fileName
              maxSize=this.maxFileSizeMb
              default="{fileName} is too large. Maximum file size is {maxSize}MB."
            }}
          </p>
        {{/each}}
      {{/unless}}
    </div>
  </template>
}
